import React, { useState, useEffect } from 'react';
import { Modal, FormGroup, FormControl, ControlLabel, Button, Well, Glyphicon } from 'react-bootstrap';
import { TimePicker, DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from 'styled-components';
import { getMonitors, makeTimelapseRequest, estimateTimelapse } from '../utils/bedrockAPI';
import './TimelapseModal.css';
import { addDaysToDate } from '../utils/Common';
import LoaderButton from './LoaderButton';

const CustomTimePicker = styled(TimePicker)`
    & .MuiInputBase-input {
        font-size: 24px;
        border: 1px solid #CCC;
        padding: 5px;
        cursor: pointer;
        text-align: center;
        border-radius: 6px;
    }
    
    & .MuiInputBase-input:hover {
        background-color: #F5F5F5
    }
`;

const CustomDatePicker = styled(DatePicker)`
    & .MuiInputBase-input {
        font-size: 24px;
        border: 1px solid #CCC;
        padding: 5px;
        cursor: pointer;
        text-align: center;
        border-radius: 6px;
    }
    
    & .MuiInputBase-input:hover {
        background-color: #F5F5F5
    }
`;

const HelpIcon = styled(Glyphicon)`
    margin-left: 8px;
    margin-bottom: 5px;
    color: #6c757d;
    cursor: pointer;
    font-size: 20px;
`;

const LabelContainer = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 5px;
`;

const HelpWell = styled(Well)`
    margin-top: 5px;
    margin-bottom: 15px;
    font-size: 14px;
    color: #666;
`;

const US_TIMEZONES = [
    'America/New_York',      // Eastern Time
    'America/Chicago',       // Central Time
    'America/Denver',        // Mountain Time
    'America/Los_Angeles',   // Pacific Time
    'America/Anchorage',     // Alaska Time
    'Pacific/Honolulu'       // Hawaii Time
];

const StyledModalTitle = styled(Modal.Title)`
    font-family: inherit;
    font-size: 24px;
    font-weight: 500;
`;

const getTimezoneOffset = (timezone) => {
    const date = new Date();
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const offset = (tzDate - utcDate) / (1000 * 60 * 60);
    return Math.round(offset);
};

const getHighResMonitorId = (selectedMonitorId, allMonitors) => {
    if (!selectedMonitorId || !allMonitors) return null;
    
    // Find which group (low/medium/high) contains our selected monitor
    let selectedGroup = null;
    
    for(let view of Object.values(allMonitors)) {
        if(view.medium.monitorId === selectedMonitorId) {
            selectedGroup = view;
            break;
        }
    }

    if (!selectedGroup) return null;

    // Find the high resolution version of the same view
    return selectedGroup.high.monitorId;
};

export default function TimelapseModal({ show, onHide, cap, monitorImages }) {
    const [startDate, setStartDate] = useState(addDaysToDate(new Date(),-14));
    const [endDate, setEndDate] = useState(new Date());
    const [startTime, setStartTime] = useState(new Date().setHours(7, 0, 0, 0));
    const [endTime, setEndTime] = useState(new Date().setHours(18, 0, 0, 0));
    const [selectedMonitor, setSelectedMonitor] = useState(null);
    const [timezone, setTimezone] = useState(() => {
        const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return US_TIMEZONES.includes(userTz) ? userTz : 'America/New_York';
    });
    const [name, setName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isEstimating, setIsEstimating] = useState(false);
    const [error, setError] = useState(null);
    const [showNameHelp, setShowNameHelp] = useState(false);
    const [showDateHelp, setShowDateHelp] = useState(false);
    const [showTimeHelp, setShowTimeHelp] = useState(false);
    const [showTimezoneHelp, setShowTimezoneHelp] = useState(false);
    const [showViewHelp, setShowViewHelp] = useState(false);
    const [allMonitors, setAllMonitors] = useState(null);
    const [estimate, setEstimate] = useState(null);

    useEffect(() => {
        async function findMonitors() {
            const monitors = await getMonitors(cap.capId);
            setAllMonitors(monitors);
        }
        if(cap) {
            findMonitors();
        }
    }, [cap]);

    const handleSubmit = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const highResMonitorId = getHighResMonitorId(selectedMonitor, allMonitors);
            if (!highResMonitorId) {
                setError('Could not find high resolution version of selected view');
                setIsLoading(false);
                return;
            }

            const request = {
                capId: cap.capId,
                monitorId: highResMonitorId,
                startDate: startDate.toISOString().split('T')[0]+"T00:00:00Z",
                endDate: endDate.toISOString().split('T')[0]+"T00:00:00Z",
                startHour: new Date(startTime).getHours(),
                endHour: new Date(endTime).getHours(),
                timeZone: getTimezoneOffset(timezone),
                name: name || `Timelapse ${cap.name}`
            };
            console.log(request);
            await makeTimelapseRequest(request);
            onHide();
        } catch (err) {
            setError('Failed to create timelapse request. Please try again.');
            console.log(err);
        }
        setIsLoading(false);
    };

    const handleEstimate = async () => {
        setIsEstimating(true);
        setError(null);
        try {
            const highResMonitorId = getHighResMonitorId(selectedMonitor, allMonitors);
            if (!highResMonitorId) {
                setError('Could not find high resolution version of selected view');
                setIsEstimating(false);
                return;
            }

            const estimate = await estimateTimelapse(
                highResMonitorId,
                startDate.toISOString().split('T')[0]+"T00:00:00Z",
                endDate.toISOString().split('T')[0]+"T00:00:00Z",
                new Date(startTime).getHours(),
                new Date(endTime).getHours(),
                getTimezoneOffset(timezone)
            );
            setEstimate(estimate);
        } catch (err) {
            setError('Failed to estimate timelapse. Please try again.');
            console.log(err);
        }
        setIsEstimating(false);
    };

    const formatTime = (minutes) => {
        if (minutes < 60) {
            return `${Math.round(minutes)} minutes`;
        }
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        return `${hours} hour${hours !== 1 ? 's' : ''}${remainingMinutes > 0 ? ` and ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}` : ''}`;
    };

    const formatLength = (seconds) => {
        if (seconds < 60) {
            return `${Math.round(seconds)} seconds`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.round(seconds % 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''}${remainingSeconds > 0 ? ` and ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}` : ''}`;
    };

    return (
        <Modal show={show} onHide={onHide} bsSize="large" enforceFocus={false}>
            <Modal.Header closeButton>
                <StyledModalTitle>Configure Timelapse for {cap.name}</StyledModalTitle>
            </Modal.Header>
            <Modal.Body>
                <FormGroup>
                    <LabelContainer>
                        <ControlLabel>Timelapse Name</ControlLabel>
                        <HelpIcon 
                            glyph="question-sign" 
                            onClick={() => setShowNameHelp(!showNameHelp)}
                        />
                    </LabelContainer>
                    {showNameHelp && (
                        <HelpWell>
                            Give your timelapse a descriptive name to help identify it later
                        </HelpWell>
                    )}
                    <FormControl
                        type="text"
                        placeholder={`Timelapse ${cap.name}`}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </FormGroup>

                <FormGroup>
                    <LabelContainer>
                        <ControlLabel>Date Range</ControlLabel>
                        <HelpIcon 
                            glyph="question-sign" 
                            onClick={() => setShowDateHelp(!showDateHelp)}
                        />
                    </LabelContainer>
                    {showDateHelp && (
                        <HelpWell>
                            Select the start and end dates for your timelapse. The longer the range, the more time it will take to process. We recommend picking an end date that is the day after your desired end date.
                        </HelpWell>
                    )}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <CustomDatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={setStartDate}
                                format="MM/dd/yyyy"
                                slot={{dialog:{disableEnforceFocus:true}}}
                            />
                            <CustomDatePicker
                                label="End Date"
                                value={endDate}
                                onChange={setEndDate}
                                format="MM/dd/yyyy"
                                slot={{dialog:{disableEnforceFocus:true}}}
                            />
                        </MuiPickersUtilsProvider>
                    </div>
                </FormGroup>

                <FormGroup>
                    <LabelContainer>
                        <ControlLabel>Time Range</ControlLabel>
                        <HelpIcon 
                            glyph="question-sign" 
                            onClick={() => setShowTimeHelp(!showTimeHelp)}
                        />
                    </LabelContainer>
                    {showTimeHelp && (
                        <HelpWell>
                            Select the hours of the day you want to include in your timelapse. The timelapse will look better if you don't include night time hours.  
                        </HelpWell>
                    )}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <CustomTimePicker
                                label="Start Time"
                                value={startTime}
                                onChange={setStartTime}
                                format="hh:mm a"
                                slot={{dialog:{disableEnforceFocus:true}}}
                            />
                            <CustomTimePicker
                                label="End Time"
                                value={endTime}
                                onChange={setEndTime}
                                format="hh:mm a"
                                slot={{dialog:{disableEnforceFocus:true}}}
                            />
                        </MuiPickersUtilsProvider>
                    </div>
                </FormGroup>

                <FormGroup>
                    <LabelContainer>
                        <ControlLabel>Timezone</ControlLabel>
                        <HelpIcon 
                            glyph="question-sign" 
                            onClick={() => setShowTimezoneHelp(!showTimezoneHelp)}
                        />
                    </LabelContainer>
                    {showTimezoneHelp && (
                        <HelpWell>
                            Select your local timezone to ensure the timelapse captures the correct hours of the day
                        </HelpWell>
                    )}
                    <FormControl
                        componentClass="select"
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                    >
                        {US_TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>
                                {tz.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')}
                            </option>
                        ))}
                    </FormControl>
                </FormGroup>

                <FormGroup>
                    <LabelContainer>
                        <ControlLabel>Select View</ControlLabel>
                        <HelpIcon 
                            glyph="question-sign" 
                            onClick={() => setShowViewHelp(!showViewHelp)}
                        />
                    </LabelContainer>
                    {showViewHelp && (
                        <HelpWell>
                            Choose which camera view you want to use for the timelapse. We'll pull the highest quality images from the selected view.
                        </HelpWell>
                    )}
                    <div className="monitor-grid">
                        {monitorImages && Object.entries(monitorImages)
                            .filter(([monitorId, monitorData]) => {
                                if (cap.architecture && cap.architecture.includes('2CAM')) {
                                    return monitorData.id <= 2;
                                }
                                return true;
                            })
                            .map(([monitorId, monitorData]) => (
                                <div 
                                    key={monitorId}
                                    className={`monitor-option ${selectedMonitor === monitorId ? 'selected' : ''}`}
                                    onClick={() => setSelectedMonitor(monitorId)}
                                >
                                    <img src={monitorData.url} alt={`Monitor ${monitorData.id}`} />
                                    <div className="monitor-label">
                                        Monitor {monitorData.id}
                                    </div>
                                </div>
                            ))}
                    </div>
                </FormGroup>

                {selectedMonitor && (
                    <FormGroup>
                        <LoaderButton
                            bsStyle="info"
                            bsSize="large"
                            onClick={handleEstimate}
                            isLoading={isEstimating}
                            disabled={!selectedMonitor}
                            block
                            style={{ marginBottom: '20px' }}
                        >
                            Estimate Timelapse
                        </LoaderButton>
                        {estimate && (
                            <Well>
                                <div style={{ fontSize: '16px', marginBottom: '10px' }}>
                                    <strong>Estimated Processing Time:</strong> {formatTime(estimate.estimatedCompletionTime)}
                                </div>
                                <div style={{ fontSize: '16px', marginBottom: '10px' }}>
                                    <strong>Estimated Timelapse Length:</strong> {formatLength(estimate.estimatedLength)}
                                </div>
                                <div style={{ fontSize: '16px' }}>
                                    <strong>Number of Images:</strong> {estimate.count.toLocaleString()}
                                </div>
                            </Well>
                        )}
                    </FormGroup>
                )}

                {error && <div className="error-message">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onHide}>Cancel</Button>
                <Button 
                    bsStyle="primary" 
                    onClick={handleSubmit}
                    disabled={isLoading || !selectedMonitor}
                >
                    {isLoading ? 'Creating...' : 'Create Timelapse'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
} 